import DOMPurify from 'dompurify';

const CLIPBOARD_ALLOWED_TAGS = [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    's',
    'u',
    'h1',
    'h2',
    'ul',
    'ol',
    'li',
    'blockquote',
    // Carries the font-size mark; see keepOnlyFontSizeStyles.
    'span',
];

/**
 * Strips every inline style declaration except `font-size`.
 *
 * The sanitizers around rich text pass `ALLOWED_ATTR: []`, which was doing two
 * jobs at once: keeping hrefs/classes out, and discarding Word's `mso-*` style
 * payload. The font-size mark needs `style` to survive, so that second job moves
 * here — a `style` attribute now reaches the output holding at most a font-size.
 *
 * Also drops spans left holding nothing, so an unsized paste stays clean HTML.
 */
export const keepOnlyFontSizeStyles = (value: string): string => {
    if (!value || !value.includes('style')) return value;

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(value, 'text/html');

        doc.body.querySelectorAll('[style]').forEach((element) => {
            const fontSize = (element as HTMLElement).style.fontSize;

            element.removeAttribute('style');

            if (fontSize) (element as HTMLElement).style.fontSize = fontSize;
        });

        doc.body.querySelectorAll('span:not([style])').forEach((span) => {
            span.replaceWith(...Array.from(span.childNodes));
        });

        return doc.body.innerHTML;
    } catch {
        return value;
    }
};

const sanitizeClipboardHtml = (value: string) =>
    keepOnlyFontSizeStyles(
        DOMPurify.sanitize(value, {
            ALLOWED_TAGS: CLIPBOARD_ALLOWED_TAGS,
            ALLOWED_ATTR: ['style'],
        }),
    );

const collapseWhitespace = (value: string) => value.replace(/\s+/g, ' ');

const cleanupSerializedText = (value: string) =>
    value
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

const indentNestedLines = (value: string, prefix = '   ') =>
    value
        .split('\n')
        .map((line) => (line ? `${prefix}${line}` : line))
        .join('\n');

const serializeChildren = (node: Node, withinListItem = false): string =>
    Array.from(node.childNodes)
        .map((child) => serializeNode(child, withinListItem))
        .join('');

const serializeListItem = (element: HTMLElement, prefix: string): string => {
    let inlineContent = '';
    let nestedContent = '';

    Array.from(element.childNodes).forEach((child) => {
        if (
            child.nodeType === Node.ELEMENT_NODE &&
            ['UL', 'OL'].includes((child as HTMLElement).tagName)
        ) {
            nestedContent += serializeNode(child);
            return;
        }

        inlineContent += serializeNode(child, true);
    });

    const normalizedInline = cleanupSerializedText(inlineContent);
    const normalizedNested = cleanupSerializedText(nestedContent);

    if (!normalizedInline && !normalizedNested) return '';

    if (!normalizedNested) {
        return `${prefix}${normalizedInline}`.trimEnd();
    }

    const nestedLines = indentNestedLines(normalizedNested);

    if (!normalizedInline) {
        return `${prefix.trimEnd()}\n${nestedLines}`;
    }

    return `${prefix}${normalizedInline}\n${nestedLines}`;
};

const serializeList = (element: HTMLElement): string => {
    const isOrdered = element.tagName === 'OL';
    const items = Array.from(element.children)
        .filter((child) => child.tagName === 'LI')
        .map((child, index) =>
            serializeListItem(child as HTMLElement, isOrdered ? `${index + 1}. ` : '- '),
        )
        .filter(Boolean);

    if (items.length === 0) return '';

    return `${items.join('\n')}\n\n`;
};

const serializeNode = (node: Node, withinListItem = false): string => {
    if (node.nodeType === Node.TEXT_NODE) {
        return collapseWhitespace(node.textContent || '');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }

    const element = node as HTMLElement;

    switch (element.tagName) {
        case 'BR':
            return '\n';
        case 'UL':
        case 'OL':
            return serializeList(element);
        case 'P':
        case 'H1':
        case 'H2':
        case 'BLOCKQUOTE': {
            const content = cleanupSerializedText(serializeChildren(element, withinListItem));

            if (!content) return '';

            return withinListItem ? `${content}\n` : `${content}\n\n`;
        }
        case 'LI':
            return serializeListItem(element, withinListItem ? '' : '- ');
        default:
            return serializeChildren(element, withinListItem);
    }
};

const MEANINGFUL_PARAGRAPH_CONTENT_SELECTOR = 'img,video,audio,iframe,embed,object,table,hr';

const isEmptyRichTextParagraph = (element: Element) =>
    element.tagName === 'P' &&
    !element.querySelector(MEANINGFUL_PARAGRAPH_CONTENT_SELECTOR) &&
    (element.textContent ?? '').replace(/\u00a0/g, ' ').trim().length === 0;

// TipTap's TrailingNode extension keeps an empty paragraph at the end of the
// document, so saved HTML ends with `<p></p>` and renders as a phantom blank
// line in the preview. Interior blank lines are intentional user content and
// are preserved.
export const stripTrailingEmptyParagraphs = (value: string) => {
    if (!value) return value;

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(value, 'text/html');
        let removed = false;

        while (doc.body.lastElementChild && isEmptyRichTextParagraph(doc.body.lastElementChild)) {
            doc.body.lastElementChild.remove();
            removed = true;
        }

        return removed ? doc.body.innerHTML : value;
    } catch {
        return value;
    }
};

export const normalizeRichTextValue = (value: string) => {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(value, 'text/html');
        const textContent = doc.body.textContent || '';

        if (textContent.trim().length === 0) return '';

        return stripTrailingEmptyParagraphs(value);
    } catch {
        return value;
    }
};

const richTextHtmlToPlainText = (value: string) => {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sanitizeClipboardHtml(value), 'text/html');

        return cleanupSerializedText(serializeChildren(doc.body));
    } catch {
        return value;
    }
};

export const copyRichTextToClipboard = async (value: string) => {
    const sanitizedHtml = sanitizeClipboardHtml(value);
    const plainText = richTextHtmlToPlainText(sanitizedHtml);

    if (!plainText) return;

    if (
        typeof globalThis.ClipboardItem !== 'undefined' &&
        typeof navigator.clipboard.write === 'function'
    ) {
        try {
            await navigator.clipboard.write([
                new globalThis.ClipboardItem({
                    'text/html': new Blob([sanitizedHtml], {
                        type: 'text/html',
                    }),
                    'text/plain': new Blob([plainText], {
                        type: 'text/plain',
                    }),
                }),
            ]);
            return;
        } catch {
            // Fallback to plain text below.
        }
    }

    await navigator.clipboard.writeText(plainText);
};

/**
 * Plain, single-line text for a rich-text value that is being shown as a
 * **label** — a breadcrumb, a select option, a `title` attribute.
 *
 * `TableBlock.description` is rich text by design: it is the table's caption,
 * edited through the floating editor, so bolding the instrument name is a
 * legitimate thing for a user to do and the field really does hold
 * `<p><strong>WISC-IV</strong></p>`. The api seeds the same shape (the template
 * catalog migration, and `test-consolidation.service`). Wherever that value is
 * printed as text instead of rendered, it has to come through here first.
 *
 * Distinct from `richTextHtmlToPlainText`, which keeps block structure for the
 * clipboard; a label needs one line.
 */
const LABEL_BLOCK_BOUNDARY = /<\/(?:p|div|h[1-6]|li|tr|blockquote|section|article)>|<br\s*\/?>/gi;

export const richTextToPlainLabel = (value?: string | null): string => {
    if (!value) return '';

    // Only pay for a parse when the value actually looks like markup. Without
    // this, a plain caption such as "Escore <70" loses its tail to the HTML
    // parser — and most descriptions are plain text, so this is the hot path.
    if (!/<[a-z][\s\S]*>/i.test(value)) return value.trim();

    try {
        // `textContent` runs adjacent blocks together — `<p>A</p><p>B</p>`
        // would read "AB" — so block boundaries are widened into whitespace
        // before parsing, and collapsed back down after.
        const spaced = value.replace(LABEL_BLOCK_BOUNDARY, '$& ');
        const doc = new DOMParser().parseFromString(spaced, 'text/html');
        // `\s` covers the non-breaking spaces Tiptap leaves behind.
        return collapseWhitespace(doc.body.textContent ?? '').trim();
    } catch {
        return value.trim();
    }
};

/**
 * The **first block** of a rich-text value, as a one-line label.
 *
 * `TableBlock.description` is one field doing two jobs: its first paragraph is
 * what everyone calls the instrument, and anything after it is the description
 * the psychologist wrote about the application. Wherever the product needs the
 * *name* — the tools panel's header, the rename field — it needs this and not
 * the whole caption run together on one line.
 *
 * Pairs with `renameRichTextLabel` below, which writes back into that same
 * first block: what this reads is exactly what that one replaces.
 */
export const richTextFirstBlockLabel = (value?: string | null): string => {
    if (!value) return '';
    if (!/<[a-z][\s\S]*>/i.test(value)) return value.trim();

    try {
        const first = new DOMParser().parseFromString(value, 'text/html').body.firstElementChild;
        if (!first) return richTextToPlainLabel(value);

        // A caption that opens with an empty paragraph would otherwise read as
        // unnamed; falling back to the whole value keeps a name on screen.
        return richTextToPlainLabel(first.innerHTML) || richTextToPlainLabel(value);
    } catch {
        return richTextToPlainLabel(value);
    }
};

/**
 * Writes `label` back into a rich-text value that is being **renamed**.
 *
 * A caption is not always just a name (see `richTextFirstBlockLabel` above), so
 * the rename touches as little as possible — only the first block is rewritten,
 * everything after it stays, and a first block that is nothing but one marked
 * run (`<p><strong>WISC-IV</strong></p>`, the shape the catalog seeds) keeps
 * that mark instead of coming back unstyled.
 */
export const renameRichTextLabel = (value: string | null | undefined, label: string): string => {
    // Plain in, plain out: most captions never went through the editor.
    if (!value || !/<[a-z][\s\S]*>/i.test(value)) return label;

    try {
        const doc = new DOMParser().parseFromString(value, 'text/html');
        const firstBlock = doc.body.firstElementChild;

        if (!firstBlock) return label;

        // Descend while the block is a single wrapper around its child — that
        // child is the run holding the name, and its tag is the mark to keep.
        let target = firstBlock;
        while (
            target.children.length === 1 &&
            (target.textContent ?? '').trim() === (target.children[0].textContent ?? '').trim()
        ) {
            target = target.children[0];
        }

        target.textContent = label;

        return doc.body.innerHTML;
    } catch {
        return label;
    }
};
